import { useEffect, useRef, useState } from "react";
import type { PublicWeather } from "@verity-connect/contracts";

export function WeatherPill({ eventSlug, timeZone, dayLabel }: { eventSlug: string; timeZone: string; dayLabel: string }) {
  const [weather, setWeather] = useState<PublicWeather | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetch(`/api/public/events/${encodeURIComponent(eventSlug)}/weather`, { signal: controller.signal })
      .then(async (response) => response.ok ? response.json() as Promise<PublicWeather> : null)
      .then((body) => { if (body) setWeather(body); })
      .catch(() => undefined);
    return () => controller.abort();
  }, [eventSlug]);

  if (!weather) return null;
  const notable = notableWeather(weather, timeZone);
  const condition = conditionLabel(weather.current.conditionCode);
  const note = notable?.text ?? null;
  return (
    <>
      <button
        className={`weather-pill is-${notable?.tone ?? "calm"}`}
        type="button"
        aria-label={`Venue weather: ${Math.round(weather.current.temperatureF)} degrees, ${condition}. Open details.`}
        onClick={() => dialog.current?.showModal()}
      >
        <WeatherIcon code={weather.current.conditionCode} />
        <strong>{Math.round(weather.current.temperatureF)}°</strong>
        <span>{note ?? condition}</span>
      </button>

      <dialog ref={dialog} className="weather-dialog" aria-labelledby="weather-title">
        <div className="weather-sheet">
          <header>
            <div><p>Campus forecast · {dayLabel}</p><h2 id="weather-title">{Math.round(weather.current.temperatureF)}° and {condition.toLowerCase()}</h2><span>Feels like {Math.round(weather.current.feelsLikeF)}° · High {Math.round(weather.today.highF)}° / Low {Math.round(weather.today.lowF)}°</span></div>
            <form method="dialog"><button type="submit" aria-label="Close weather details">×</button></form>
          </header>

          {weather.alerts.length > 0 && <section className="weather-alerts" aria-labelledby="weather-alerts-title"><h3 id="weather-alerts-title">Weather alerts</h3>{weather.alerts.map((alert) => <article key={alert.id}><strong>{alert.description}</strong><span>{alert.source} · {conditionLabel(alert.severity)}</span>{alert.detailsUrl && <a href={alert.detailsUrl} target="_blank" rel="noreferrer">Read alert</a>}</article>)}</section>}

          {weather.nextHour && weather.nextHour.minutes.length > 0 && <section className="weather-next-hour" aria-labelledby="weather-next-hour-title">
            <div><h3 id="weather-next-hour-title">Next hour</h3><span>{nextHourSummary(weather)}</span></div>
            <div className="weather-minute-strip" role="img" aria-label={nextHourSummary(weather)}>
              {weather.nextHour.minutes.map((minute) => <i key={minute.startTime} style={{ height: minuteHeight(minute.intensityInchesPerHour) }} title={`${formatTime(minute.startTime, timeZone)}: ${minute.chancePercent}% chance`} />)}
            </div>
            <div className="weather-strip-axis"><span>Now</span><span>30 min</span><span>60 min</span></div>
          </section>}

          <section className="weather-hours" aria-labelledby="weather-hours-title"><h3 id="weather-hours-title">Hourly</h3><div>{weather.hours.map((hour) => <article key={hour.forecastStart}><time dateTime={hour.forecastStart}>{formatHour(hour.forecastStart, timeZone)}</time><WeatherIcon code={hour.conditionCode} /><strong>{Math.round(hour.temperatureF)}°</strong><small>{hour.precipitationChancePercent}%</small></article>)}</div></section>

          <section className="weather-details" aria-labelledby="weather-details-title"><h3 id="weather-details-title">Around campus</h3><dl>
            <div><dt>Wind</dt><dd>{windText(weather)}</dd></div>
            <div><dt>Humidity</dt><dd>{weather.current.humidityPercent}%</dd></div>
            <div><dt>Dew point</dt><dd>{Math.round(weather.current.dewPointF)}°</dd></div>
            <div><dt>UV index</dt><dd>{weather.current.uvIndex}</dd></div>
            <div><dt>Visibility</dt><dd>{weather.current.visibilityMiles} mi</dd></div>
            <div><dt>Pressure</dt><dd>{weather.current.pressureInHg} in · {conditionLabel(weather.current.pressureTrend).toLowerCase()}</dd></div>
            <div><dt>Cloud cover</dt><dd>{weather.current.cloudCoverPercent}%</dd></div>
            <div><dt>Sun</dt><dd>{sunText(weather, timeZone)}</dd></div>
          </dl></section>

          <footer>
            {weather.attribution.logoUrl && <img src={weather.attribution.logoUrl} alt={weather.attribution.serviceName} />}
            {!weather.attribution.logoUrl && <span>{weather.attribution.serviceName}</span>}
            {weather.attribution.sourcesUrl && <a href={weather.attribution.sourcesUrl} target="_blank" rel="noreferrer">Weather sources</a>}
            <small>Observed {formatTime(weather.observedAt, timeZone)}</small>
          </footer>
        </div>
      </dialog>
    </>
  );
}

/**
 * What the pill says when there is something worth saying, and how loudly.
 *
 * Three tones rather than two. A government alert is the only thing that earns red;
 * rain and wind are blue, because a teacher deciding whether to walk to the Servant
 * Center needs to notice them without being alarmed by them. Everything else is calm,
 * and a calm pill should be almost invisible.
 *
 * Every phrase here is deliberately short. The pill sits in a corner beside a heading,
 * and a sentence that has to be cut off with an ellipsis has failed at the one job it
 * had.
 */
function notableWeather(weather: PublicWeather, timeZone: string): { text: string; tone: "alert" | "wet" } | null {
  const alert = weather.alerts[0];
  // Government wording runs long — "Severe Thunderstorm Watch until 8:00 PM EDT" — and
  // the first two or three words carry it. The whole thing is in the sheet.
  if (alert) return { text: shorten(conditionLabel(alert.description)), tone: "alert" };
  if (weather.nextHour?.minutes.some((minute) => minute.intensityInchesPerHour > 0)) {
    return { text: "Rain soon", tone: "wet" };
  }
  const wet = weather.hours.slice(0, 6).find((hour) => hour.precipitationType.toLowerCase() !== "clear" && hour.precipitationType.toLowerCase() !== "none" && hour.precipitationChancePercent >= 50);
  if (wet) return { text: `Rain by ${formatHour(wet.forecastStart, timeZone)}`, tone: "wet" };
  if (weather.current.windMph >= 20 || (weather.current.windGustMph ?? 0) >= 30) {
    return { text: "Windy", tone: "wet" };
  }
  return null;
}

function shorten(value: string): string {
  const words = value.split(/\s+/u).filter(Boolean);
  return words.length <= 3 ? value : `${words.slice(0, 3).join(" ")}…`;
}

function conditionLabel(value: string): string {
  return value.replace(/([a-z])([A-Z])/gu, "$1 $2").replace(/[_-]+/gu, " ").replace(/^\w/u, (letter) => letter.toUpperCase());
}

type WeatherShape = "thunder" | "snow" | "rain" | "fog" | "cloud" | "wind" | "sun";

function weatherShape(code: string): WeatherShape {
  const value = code.toLowerCase();
  if (value.includes("thunder")) return "thunder";
  if (value.includes("snow") || value.includes("flurr") || value.includes("sleet")) return "snow";
  if (value.includes("rain") || value.includes("drizzle") || value.includes("shower")) return "rain";
  if (value.includes("fog") || value.includes("haze") || value.includes("smoke")) return "fog";
  if (value.includes("wind") || value.includes("breez")) return "wind";
  if (value.includes("cloud") || value.includes("overcast")) return "cloud";
  return "sun";
}

/**
 * Drawn rather than typed.
 *
 * These were Unicode characters — ⛈ ☂ ≋ ↝ — and on a phone the system font has no
 * glyph for several of them, so the pill showed an empty box. A box where the weather
 * should be is worse than no weather at all. Shapes cannot go missing, they take the
 * colour they are given, and they stay sharp at any size.
 */
function WeatherIcon({ code }: { code: string }) {
  const shape = weatherShape(code);
  return (
    <svg className="weather-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" data-shape={shape}>
      {shape === "sun" && <>
        <circle className="weather-icon-sun" cx="12" cy="12" r="4.6" />
        <path className="weather-icon-rays" d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.3 5.3l1.6 1.6M17.1 17.1l1.6 1.6M18.7 5.3l-1.6 1.6M6.9 17.1l-1.6 1.6" />
      </>}
      {shape !== "sun" && <path className="weather-icon-cloud" d="M7.2 17h9.3a3.7 3.7 0 0 0 .3-7.4 5.4 5.4 0 0 0-10.2-1.4A4 4 0 0 0 7.2 17Z" />}
      {shape === "rain" && <path className="weather-icon-fall" d="M9 19.4l-.8 1.9M13 19.4l-.8 1.9M17 19.4l-.8 1.9" />}
      {/* Held inside the 24-unit box. Drawn lower it is quietly clipped, and a rounded
          stroke cap reaches almost a unit past wherever the path ends. */}
      {shape === "thunder" && <path className="weather-icon-bolt" d="M13 17.9l-2.5 3.1h2l-.5 1.7" />}
      {shape === "snow" && <path className="weather-icon-fall" d="M9 20.4h.02M13 20.4h.02M17 20.4h.02" />}
      {shape === "fog" && <path className="weather-icon-fall" d="M6.6 20.2h10.8M8.8 22.6h6.4" />}
      {shape === "wind" && <path className="weather-icon-fall" d="M6 20.4h8.4a1.8 1.8 0 1 0-1.8-1.8" />}
    </svg>
  );
}

function minuteHeight(intensity: number): string { return `${Math.max(3, Math.min(100, intensity * 800))}%`; }
function nextHourSummary(weather: PublicWeather): string {
  const wet = weather.nextHour?.minutes.filter((minute) => minute.intensityInchesPerHour > 0) ?? [];
  if (wet.length === 0) return "No rain expected";
  const peak = Math.max(...wet.map((minute) => minute.chancePercent));
  return `Rain possible · up to ${peak}%`;
}
function formatHour(value: string, timeZone: string): string { return new Intl.DateTimeFormat("en-US", { hour: "numeric", timeZone }).format(new Date(value)); }
function formatTime(value: string, timeZone: string): string { return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone }).format(new Date(value)); }
function windText(weather: PublicWeather): string { return weather.current.windGustMph === null ? `${weather.current.windMph} mph` : `${weather.current.windMph} mph · gusts ${weather.current.windGustMph}`; }
function sunText(weather: PublicWeather, timeZone: string): string {
  if (!weather.today.sunrise || !weather.today.sunset) return "Not available";
  return `${formatTime(weather.today.sunrise, timeZone)} / ${formatTime(weather.today.sunset, timeZone)}`;
}
